import { type VariantProps, cva } from "class-variance-authority";
import React from "react";
import { cn } from "@/lib/utils";

const typographyVariants = cva("text-foreground", {
  defaultVariants: {
    size: "default",
    variant: "p",
  },
  variants: {
    size: {
      default: "text-base font-normal",
    },
    variant: {
      blockquote: "mt-6 border-l-2 pl-6 italic",
      h0: "text-[32px] leading-[40px]",
      h2: "text-[22px] leading-[28px] first:mt-0",
      h3: "scroll-m-20 text-2xl font-semibold tracking-tight",
      list: "my-6 ml-6 list-disc [&>li]:mt-2",
      p: "text-xs",
      s2: "text-base leading-4.5 font-semibold",
      sh2: "text-sm leading-5 font-semibold text-brand-blue-600",
      title: "text-2xl leading md:text-[64px] font-bold md:leading-18.5 lg:text-[64px]",
    },
  },
});

export interface TypographyProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof typographyVariants> {
  as?: React.ElementType;
}

const elementMap: Record<string, React.ElementType> = {
  blockquote: "blockquote",
  h0: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  list: "ul",
  p: "p",
  s2: "h2",
  sh2: "h2",
  title: "h1",
};

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ className, variant, size, as, ...props }, ref) => {
    const Comp = as || (variant ? elementMap[variant] || "p" : "p");
    return (
      <Comp
        className={cn(typographyVariants({ className, size, variant }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Typography.displayName = "Typography";

export { Typography, typographyVariants };

export default Typography;
